<template>
  <div class="relative">
    <q-inner-loading :showing="isLoading">
      <q-spinner-gears size="100px" color="primary" />
    </q-inner-loading>

    <!-- Error Banner -->
    <q-banner
      v-if="error"
      inline-actions
      rounded
      class="bg-gradient-to-r from-orange-500 to-red-500 text-white m-4 rounded-xl"
    >
      <q-icon name="error" color="white" size="2em" />
      <span class="ml-2 font-semibold">{{ error }}</span>
    </q-banner>

    <!-- Main Content -->
    <div class="p-6">
          <q-banner
            v-if="!isNative"
            class="bg-orange-100 text-orange-800 border border-orange-200 rounded-lg mb-6"
            rounded
            inline-actions
          >
            <div class="flex items-center gap-2">
              <q-icon name="warning" color="orange-7" />
              <span>当前为 Web 环境，原生能力不可用</span>
            </div>
          </q-banner>

          <!-- Load Button -->
          <div class="mb-6">
            <button
              @click="loadAllClasses"
              :disabled="loading || !isNative"
              class="w-full px-6 py-3 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 disabled:from-gray-400 disabled:to-gray-500 disabled:cursor-not-allowed text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 disabled:transform-none"
            >
              <div class="flex items-center justify-center gap-3">
                <q-spinner-dots v-if="loading" class="text-white" />
                <q-icon v-else name="refresh" class="text-xl" />
                <span>{{ loading ? "加载中..." : "加载所有属性类" }}</span>
              </div>
            </button>
          </div>

          <div class="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <!-- Left: Class Selection -->
            <div
              class="bg-gradient-to-br from-white to-gray-50 dark:from-gray-800 dark:to-gray-700 rounded-xl p-6 shadow-lg border border-gray-200 dark:border-gray-600"
            >
              <div class="flex items-center gap-3 mb-4">
                <div
                  class="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center"
                >
                  <q-icon name="folder" class="text-white text-xl" />
                </div>
                <h2 class="text-xl font-bold text-gray-800 dark:text-gray-100">
                  第一层级选择
                </h2>
              </div>

              <div class="space-y-2 max-h-[600px] overflow-y-auto">
                <button
                  v-for="className in classNames"
                  :key="className"
                  @click="selectClass(className)"
                  :class="
                    selectedClass === className
                      ? 'bg-gradient-to-r from-blue-500 to-indigo-500 text-white'
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-600'
                  "
                  class="w-full px-4 py-3 rounded-lg font-semibold text-left transition-all duration-200"
                >
                  <div class="flex items-center justify-between">
                    <span>{{ className }}</span>
                    <span
                      v-if="classProperties[className]"
                      class="px-2 py-1 bg-white/20 dark:bg-black/20 rounded text-xs"
                    >
                      {{ classProperties[className].length }}
                    </span>
                  </div>
                </button>
              </div>
            </div>

            <!-- Middle: Property Selection -->
            <div
              class="bg-gradient-to-br from-white to-gray-50 dark:from-gray-800 dark:to-gray-700 rounded-xl p-6 shadow-lg border border-gray-200 dark:border-gray-600"
            >
              <div class="flex items-center gap-3 mb-4">
                <div
                  class="w-10 h-10 bg-gradient-to-br from-green-500 to-green-600 rounded-lg flex items-center justify-center"
                >
                  <q-icon name="list" class="text-white text-xl" />
                </div>
                <h2 class="text-xl font-bold text-gray-800 dark:text-gray-100">
                  第二层级选择
                </h2>
              </div>

              <div
                v-if="!selectedClass"
                class="text-center text-gray-500 dark:text-gray-400 py-8"
              >
                请先选择第一层级
              </div>

              <div v-else>
                <!-- Search Input -->
                <div class="mb-4">
                  <q-input
                    v-model="searchKeyword"
                    outlined
                    dense
                    placeholder="搜索属性名称或ID..."
                    bg-color="white"
                    class="w-full"
                  >
                    <template v-slot:prepend>
                      <q-icon name="search" />
                    </template>
                    <template v-slot:append>
                      <q-icon
                        v-if="searchKeyword"
                        name="clear"
                        @click="searchKeyword = ''"
                        class="cursor-pointer"
                      />
                    </template>
                  </q-input>
                </div>

                <div
                  v-if="filteredProperties.length === 0"
                  class="text-center text-gray-500 dark:text-gray-400 py-8"
                >
                  {{ loadingProperties ? "加载中..." : searchKeyword ? "未找到匹配的属性" : "该层级下暂无属性" }}
                </div>

                <div v-else class="space-y-2 max-h-[600px] overflow-y-auto">
                  <div class="text-xs text-gray-500 dark:text-gray-400 mb-2 px-2">
                    共找到 {{ filteredProperties.length }} 个属性
                  </div>
                  <button
                    v-for="prop in filteredProperties"
                    :key="prop.name"
                    @click="selectProperty(prop)"
                    :class="
                      selectedProperty?.name === prop.name
                        ? 'bg-gradient-to-r from-green-500 to-emerald-500 text-white'
                        : 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-600'
                    "
                    class="w-full px-4 py-3 rounded-lg font-semibold text-left transition-all duration-200"
                  >
                    <div class="flex flex-col gap-1">
                      <span class="text-sm font-bold">{{ prop.name }}</span>
                      <span class="text-xs opacity-75">ID: 0x{{ prop.id.toString(16).toUpperCase() }}</span>
                    </div>
                  </button>
                </div>
              </div>
            </div>

            <!-- Right: Property Details -->
            <div
              class="bg-gradient-to-br from-white to-gray-50 dark:from-gray-800 dark:to-gray-700 rounded-xl p-6 shadow-lg border border-gray-200 dark:border-gray-600"
            >
              <div class="flex items-center gap-3 mb-4">
                <div
                  class="w-10 h-10 bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg flex items-center justify-center"
                >
                  <q-icon name="info" class="text-white text-xl" />
                </div>
                <h2 class="text-xl font-bold text-gray-800 dark:text-gray-100">
                  属性详情
                </h2>
              </div>

              <div
                v-if="!selectedProperty"
                class="text-center text-gray-500 dark:text-gray-400 py-8"
              >
                请选择一个属性查看详情
              </div>

              <div v-else class="space-y-4">
                <!-- Property Name -->
                <div
                  class="bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/30 dark:to-indigo-900/30 rounded-xl p-4 border border-blue-200 dark:border-blue-700/50"
                >
                  <div class="text-sm font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide mb-2">
                    属性名称
                  </div>
                  <div class="text-lg font-bold text-blue-700 dark:text-blue-300 font-mono">
                    {{ selectedProperty.name }}
                  </div>
                </div>

                <!-- Property ID -->
                <div
                  class="bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-900/30 dark:to-emerald-900/30 rounded-xl p-4 border border-green-200 dark:border-green-700/50"
                >
                  <div class="text-sm font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide mb-2">
                    属性 ID
                  </div>
                  <div class="text-lg font-bold text-green-700 dark:text-green-300 font-mono">
                    0x{{ selectedProperty.id.toString(16).toUpperCase() }}
                    <span class="text-sm opacity-75 ml-2"
                      >({{ selectedProperty.id }})</span
                    >
                  </div>
                </div>

                <!-- Mapped Property Name (toString) -->
                <div
                  class="bg-gradient-to-br from-purple-50 to-pink-50 dark:from-purple-900/30 dark:to-pink-900/30 rounded-xl p-4 border border-purple-200 dark:border-purple-700/50"
                >
                  <div class="text-sm font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide mb-2">
                    VehiclePropertyMapper.toString()
                  </div>
                  <div class="text-lg font-bold text-purple-700 dark:text-purple-300 font-mono break-all">
                    {{ selectedProperty.propertyName || "未找到映射" }}
                  </div>
                </div>

                <!-- Converted ID (toId) -->
                <div
                  class="bg-gradient-to-br from-orange-50 to-red-50 dark:from-orange-900/30 dark:to-red-900/30 rounded-xl p-4 border border-orange-200 dark:border-orange-700/50"
                >
                  <div class="text-sm font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide mb-2">
                    VehiclePropertyMapper.toId()
                  </div>
                  <div class="text-lg font-bold text-orange-700 dark:text-orange-300 font-mono">
                    <span v-if="selectedProperty.propertyName">
                      0x{{ selectedProperty.convertedId.toString(16).toUpperCase() }}
                      <span class="text-sm opacity-75 ml-2"
                        >({{ selectedProperty.convertedId }})</span
                      >
                    </span>
                    <span v-else class="text-red-500">无法转换（属性名称为空）</span>
                  </div>
                </div>

                <!-- ID Match Status -->
                <div
                  :class="
                    selectedProperty.idMatch
                      ? 'bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-900/30 dark:to-emerald-900/30 border-green-200 dark:border-green-700/50'
                      : 'bg-gradient-to-br from-red-50 to-orange-50 dark:from-red-900/30 dark:to-orange-900/30 border-red-200 dark:border-red-700/50'
                  "
                  class="rounded-xl p-4 border"
                >
                  <div class="text-sm font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide mb-2">
                    ID 匹配状态
                  </div>
                  <div
                    :class="
                      selectedProperty.idMatch
                        ? 'text-green-700 dark:text-green-300'
                        : 'text-red-700 dark:text-red-300'
                    "
                    class="text-lg font-bold flex items-center gap-2"
                  >
                    <q-icon
                      :name="selectedProperty.idMatch ? 'check_circle' : 'error'"
                      size="24px"
                    />
                    <span>{{
                      selectedProperty.idMatch
                        ? "ID 匹配成功 ✓"
                        : "ID 不匹配 ✗"
                    }}</span>
                  </div>
                </div>

                <!-- Test Conversion Buttons -->
                <div class="space-y-2">
                  <button
                    @click="testIdToString"
                    :disabled="testing || !isNative"
                    class="w-full px-4 py-2 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 disabled:from-gray-400 disabled:to-gray-500 disabled:cursor-not-allowed text-white font-semibold rounded-lg transition-all duration-200"
                  >
                    <div class="flex items-center justify-center gap-2">
                      <q-spinner-dots v-if="testing" size="16px" />
                      <q-icon v-else name="swap_horiz" />
                      <span>测试 ID → Name</span>
                    </div>
                  </button>

                  <button
                    v-if="selectedProperty.propertyName"
                    @click="testStringToId"
                    :disabled="testing || !isNative"
                    class="w-full px-4 py-2 bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 disabled:from-gray-400 disabled:to-gray-500 disabled:cursor-not-allowed text-white font-semibold rounded-lg transition-all duration-200"
                  >
                    <div class="flex items-center justify-center gap-2">
                      <q-spinner-dots v-if="testing" size="16px" />
                      <q-icon v-else name="swap_horiz" />
                      <span>测试 Name → ID</span>
                    </div>
                  </button>

                  <button
                    v-if="selectedProperty.propertyName"
                    @click="getPropertyValue"
                    :disabled="gettingProperty || !isNative || isPropertySubscribed"
                    class="w-full px-4 py-2 bg-gradient-to-r from-purple-500 to-purple-600 hover:from-purple-600 hover:to-purple-700 disabled:from-gray-400 disabled:to-gray-500 disabled:cursor-not-allowed text-white font-semibold rounded-lg transition-all duration-200"
                  >
                    <div class="flex items-center justify-center gap-2">
                      <q-spinner-dots v-if="gettingProperty" size="16px" />
                      <q-icon v-else name="download" />
                      <span>获取属性值</span>
                    </div>
                  </button>

                  <button
                    v-if="selectedProperty.propertyName && !isPropertySubscribed"
                    @click="subscribePropertyValue"
                    :disabled="subscribingProperty || !isNative"
                    class="w-full px-4 py-2 bg-gradient-to-r from-teal-500 to-teal-600 hover:from-teal-600 hover:to-teal-700 disabled:from-gray-400 disabled:to-gray-500 disabled:cursor-not-allowed text-white font-semibold rounded-lg transition-all duration-200"
                  >
                    <div class="flex items-center justify-center gap-2">
                      <q-spinner-dots v-if="subscribingProperty" size="16px" />
                      <q-icon v-else name="notifications_active" />
                      <span>订阅属性值</span>
                    </div>
                  </button>

                  <button
                    v-if="selectedProperty.propertyName && isPropertySubscribed"
                    @click="unsubscribePropertyValue"
                    :disabled="subscribingProperty || !isNative"
                    class="w-full px-4 py-2 bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 disabled:from-gray-400 disabled:to-gray-500 disabled:cursor-not-allowed text-white font-semibold rounded-lg transition-all duration-200"
                  >
                    <div class="flex items-center justify-center gap-2">
                      <q-spinner-dots v-if="subscribingProperty" size="16px" />
                      <q-icon v-else name="notifications_off" />
                      <span>取消订阅</span>
                    </div>
                  </button>
                </div>

                <!-- Property Value Result -->
                <div
                  v-if="propertyValueResult"
                  class="bg-gradient-to-br from-indigo-50 to-purple-50 dark:from-indigo-900/30 dark:to-purple-900/30 rounded-xl p-4 border border-indigo-200 dark:border-indigo-700/50 space-y-3"
                >
                  <div class="flex items-center justify-between gap-2">
                    <div class="text-sm font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide">
                      属性值
                    </div>
                    <q-badge
                      v-if="isPropertySubscribed"
                      color="teal"
                      label="订阅中"
                    />
                  </div>
                  
                  <div class="space-y-2">
                    <div class="flex items-center justify-between">
                      <span class="text-sm text-gray-600 dark:text-gray-400">状态：</span>
                      <span
                        :class="
                          propertyValueResult.status === 'OK'
                            ? 'text-green-600 dark:text-green-400'
                            : propertyValueResult.status === 'TRY_AGAIN'
                            ? 'text-yellow-600 dark:text-yellow-400'
                            : 'text-red-600 dark:text-red-400'
                        "
                        class="text-sm font-semibold"
                      >
                        {{ propertyValueResult.status }}
                      </span>
                    </div>
                    
                    <div class="flex items-center justify-between">
                      <span class="text-sm text-gray-600 dark:text-gray-400">值：</span>
                      <span class="text-lg font-mono font-bold text-indigo-700 dark:text-indigo-300">
                        {{ formatPropertyValue(propertyValueResult.value) }}
                      </span>
                    </div>
                    
                    <div class="flex items-center justify-between">
                      <span class="text-sm text-gray-600 dark:text-gray-400">时间戳：</span>
                      <span class="text-xs text-gray-500 dark:text-gray-400">
                        {{ new Date(propertyValueResult.timestamp).toLocaleString() }}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from "vue";
import { useQuasar } from "quasar";
import { isNative } from "@/utils/capacitor";
import {
  getAllPropertyClasses,
  getClassProperties,
  convertIdToString,
  convertStringToId,
  PropertyInfo,
  ClassProperties,
} from "@/utils/capacitor/vehiclePropertyPlugin";
import CarPropertyClientClient, {
  CarPropertyClientValue,
  SubscribeOptionFlags,
} from "@/utils/capacitor/carPropertyClientPlugin";

const $q = useQuasar();

const isLoading = ref(false);
const loading = ref(false);
const loadingProperties = ref(false);
const testing = ref(false);
const gettingProperty = ref(false);
const subscribingProperty = ref(false);
const isPropertySubscribed = ref(false);
const subscribedPropertyName = ref<string | null>(null);
const error = ref<string>("");
const searchKeyword = ref<string>("");

const classProperties = ref<ClassProperties>({});
const selectedClass = ref<string>("");
const selectedProperty = ref<PropertyInfo | null>(null);
const propertyValueResult = ref<CarPropertyClientValue | null>(null);

const classNames = computed(() => Object.keys(classProperties.value));

const currentProperties = computed(() => {
  if (!selectedClass.value || !classProperties.value[selectedClass.value]) {
    return [];
  }
  return classProperties.value[selectedClass.value];
});

const filteredProperties = computed(() => {
  if (!searchKeyword.value.trim()) {
    return currentProperties.value;
  }

  const keyword = searchKeyword.value.trim().toLowerCase();
  return currentProperties.value.filter((prop) => {
    // 搜索属性名称（不区分大小写）
    const nameMatch = prop.name.toLowerCase().includes(keyword);
    
    // 搜索属性ID（支持十进制和十六进制）
    const idDecimal = prop.id.toString().includes(keyword);
    const idHex = prop.id.toString(16).toLowerCase().includes(keyword.toLowerCase());
    const idHexWithPrefix = `0x${prop.id.toString(16).toLowerCase()}`.includes(keyword.toLowerCase());
    
    // 搜索属性名称（如果存在）
    const propertyNameMatch = prop.propertyName?.toLowerCase().includes(keyword) || false;
    
    return nameMatch || idDecimal || idHex || idHexWithPrefix || propertyNameMatch;
  });
});


const loadAllClasses = async () => {
  if (!isNative) {
    error.value = "仅在原生环境可用";
    $q.notify({ type: "warning", message: error.value, position: "top" });
    return;
  }

  loading.value = true;
  error.value = "";

  try {
    const result = await getAllPropertyClasses();
    classProperties.value = result.classes || {};
    $q.notify({
      type: "positive",
      message: `成功加载 ${classNames.value.length} 个属性类`,
      position: "top",
    });
  } catch (err: any) {
    error.value = err?.message || "加载属性类失败";
    $q.notify({ type: "negative", message: error.value, position: "top" });
  } finally {
    loading.value = false;
  }
};

const unsubscribePropertyValueSilent = async () => {
  const name = subscribedPropertyName.value;
  if (!name || !isPropertySubscribed.value) return;
  try {
    await CarPropertyClientClient.unsubscribeProperty({ propertyNames: [name] });
  } catch (e) {
    console.warn("[VehiclePropertyMapperTest] unsubscribeProperty failed", e);
  } finally {
    isPropertySubscribed.value = false;
    subscribedPropertyName.value = null;
  }
};

const selectClass = async (className: string) => {
  await unsubscribePropertyValueSilent();
  selectedClass.value = className;
  selectedProperty.value = null;
  propertyValueResult.value = null;
  searchKeyword.value = ""; // 切换类时清空搜索

  // 如果该类还没有加载属性，则加载
  if (!classProperties.value[className] || classProperties.value[className].length === 0) {
    loadingProperties.value = true;
    try {
      const result = await getClassProperties(className);
      if (result.properties) {
        classProperties.value[className] = result.properties;
      }
    } catch (err: any) {
      $q.notify({
        type: "negative",
        message: `加载 ${className} 属性失败: ${err?.message}`,
        position: "top",
      });
    } finally {
      loadingProperties.value = false;
    }
  }
};

const selectProperty = async (prop: PropertyInfo) => {
  await unsubscribePropertyValueSilent();
  selectedProperty.value = prop;
  propertyValueResult.value = null;
};

const onCarPropertyUpdate = (data: CarPropertyClientValue) => {
  const expect = selectedProperty.value?.propertyName;
  if (!expect || !data.propertyName) return;
  if (data.propertyName.toLowerCase() !== expect.toLowerCase()) return;
  propertyValueResult.value = { ...data };
};

const subscribePropertyValue = async () => {
  if (!selectedProperty.value?.propertyName || !isNative) return;

  subscribingProperty.value = true;
  try {
    await unsubscribePropertyValueSilent();

    const propertyName = selectedProperty.value.propertyName;
    await CarPropertyClientClient.subscribeProperty(
      {
        propertyNames: [propertyName],
        updateRateMs: 50,
        eventFlags:
          SubscribeOptionFlags.UPDATE | SubscribeOptionFlags.NEED_CURRENT_VALUE,
      },
      onCarPropertyUpdate
    );
    subscribedPropertyName.value = propertyName;
    isPropertySubscribed.value = true;

    $q.notify({
      type: "positive",
      message: "已开始订阅，属性值将实时更新",
      position: "top",
    });
  } catch (err: any) {
    isPropertySubscribed.value = false;
    subscribedPropertyName.value = null;
    $q.notify({
      type: "negative",
      message: `订阅失败: ${err?.message ?? err}`,
      position: "top",
    });
  } finally {
    subscribingProperty.value = false;
  }
};

const unsubscribePropertyValue = async () => {
  if (!isNative) return;

  subscribingProperty.value = true;
  try {
    await unsubscribePropertyValueSilent();
    $q.notify({
      type: "positive",
      message: "已取消订阅",
      position: "top",
    });
  } catch (err: any) {
    $q.notify({
      type: "negative",
      message: `取消订阅失败: ${err?.message ?? err}`,
      position: "top",
    });
  } finally {
    subscribingProperty.value = false;
  }
};

const testIdToString = async () => {
  if (!selectedProperty.value || !isNative) return;

  testing.value = true;
  try {
    const result = await convertIdToString(selectedProperty.value.id);
    if (result.success) {
      selectedProperty.value.propertyName = result.propertyName;
      $q.notify({
        type: "positive",
        message: `转换成功: ${result.propertyName}`,
        position: "top",
      });
    } else {
      $q.notify({
        type: "warning",
        message: "转换失败: 未找到对应的属性名称",
        position: "top",
      });
    }
  } catch (err: any) {
    $q.notify({
      type: "negative",
      message: `转换失败: ${err?.message}`,
      position: "top",
    });
  } finally {
    testing.value = false;
  }
};

const testStringToId = async () => {
  if (!selectedProperty.value?.propertyName || !isNative) return;

  testing.value = true;
  try {
    const result = await convertStringToId(selectedProperty.value.propertyName);
    if (result.success) {
      $q.notify({
        type: "positive",
        message: `转换成功: ID = 0x${result.propertyId.toString(16).toUpperCase()}`,
        position: "top",
      });
      
      // 更新匹配状态
      if (selectedProperty.value) {
        selectedProperty.value.convertedId = result.propertyId;
        selectedProperty.value.idMatch = result.propertyId === selectedProperty.value.id;
      }
    } else {
      $q.notify({
        type: "warning",
        message: "转换失败: 未找到对应的属性ID",
        position: "top",
      });
    }
  } catch (err: any) {
    $q.notify({
      type: "negative",
      message: `转换失败: ${err?.message}`,
      position: "top",
    });
  } finally {
    testing.value = false;
  }
};

const getPropertyValue = async () => {
  if (!selectedProperty.value?.propertyName || !isNative) return;
  console.log("[BOWEN_LOG][VehiclePropertyMapperTest] ~~ getPropertyValue ~~ selectedProperty.value?.propertyName:", selectedProperty.value?.propertyName);

  gettingProperty.value = true;
  propertyValueResult.value = null;
  
  try {
    const result = await CarPropertyClientClient.getProperty({
      propertyName: selectedProperty.value.propertyName,
    });
    console.log("[BOWEN_LOG] 🚀 ~~ getPropertyValue ~~ result:", result);
    
    // 处理单个属性返回结果
    const propertyValue: CarPropertyClientValue = "properties" in result 
      ? result.properties[0] 
      : result;
    
    propertyValueResult.value = propertyValue;
    
    console.log("[VehiclePropertyMapperTest] getProperty result:", propertyValue);
    
    $q.notify({
      type: propertyValue.status === "OK" ? "positive" : "warning",
      message: `获取属性值${propertyValue.status === "OK" ? "成功" : "完成"}，状态: ${propertyValue.status}`,
      position: "top",
    });
  } catch (err: any) {
    $q.notify({
      type: "negative",
      message: `获取属性值失败: ${err?.message}`,
      position: "top",
    });
  } finally {
    gettingProperty.value = false;
  }
};

const formatPropertyValue = (value: any): string => {
  if (value === null || value === undefined) {
    return "--";
  }
  if (typeof value === "number") {
    if (value % 1 !== 0) {
      return value.toFixed(6);
    }
    return value.toString();
  }
  if (Array.isArray(value)) {
    return `[${value.join(", ")}]`;
  }
  return String(value);
};

onMounted(() => {
  if (isNative) {
    loadAllClasses();
  }
});

onUnmounted(() => {
  void unsubscribePropertyValueSilent();
});
</script>

<style scoped>
/* 自定义滚动条样式 */
.overflow-y-auto::-webkit-scrollbar {
  width: 6px;
}

.overflow-y-auto::-webkit-scrollbar-track {
  background: rgba(0, 0, 0, 0.05);
  border-radius: 10px;
}

.overflow-y-auto::-webkit-scrollbar-thumb {
  background: rgba(0, 0, 0, 0.2);
  border-radius: 10px;
}

.overflow-y-auto::-webkit-scrollbar-thumb:hover {
  background: rgba(0, 0, 0, 0.3);
}

.dark .overflow-y-auto::-webkit-scrollbar-track {
  background: rgba(255, 255, 255, 0.05);
}

.dark .overflow-y-auto::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.2);
}

.dark .overflow-y-auto::-webkit-scrollbar-thumb:hover {
  background: rgba(255, 255, 255, 0.3);
}
</style>

